import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import { FieldError } from '@/Domain/components/Shared/Field/Field';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { usePasskeys } from '@/Domain/hooks/User/usePasskeys/usePasskeys.hook';

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

export const PasskeysCard = () => {
  const { t, i18n } = useTranslation('user_profile');
  const {
    isSupported,
    passkeys,
    isLoading,
    isError,
    retry,
    addPasskey,
    isAdding,
    needsPassword,
    passwordDeleteName,
    isVerifying,
    passwordForm,
    submitPassword,
    cancelPassword,
    deletePasskey,
    deletingId,
  } = usePasskeys();
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, DATE_OPTIONS).format(new Date(iso));

  return (
    <Card data-testid="passkeys-card">
      <CardHeader className="pb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>{t('passkeys.title')}</CardTitle>
            <CardDescription className="mt-1.5">{t('passkeys.description')}</CardDescription>
          </div>
          {isSupported && !needsPassword && (
            <Button
              variant="secondary"
              size="sm"
              onClick={addPasskey}
              loading={isAdding}
              disabled={deletingId !== null}
              data-testid="passkeys-add"
            >
              <Plus />
              {t('passkeys.add')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isSupported && (
          <p className="text-sm text-muted-foreground" data-testid="passkeys-unsupported">
            {t('passkeys.unsupported')}
          </p>
        )}
        {needsPassword && (
          <form
            onSubmit={submitPassword}
            className="space-y-3 rounded-lg border border-border p-4"
            data-testid="passkeys-password-form"
          >
            <div className="space-y-1.5">
              <Label htmlFor="passkey-current-password" className="font-medium">
                {t('passkeys.password_label')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {passwordDeleteName !== undefined
                  ? t('passkeys.password_delete_hint', { name: passwordDeleteName })
                  : t('passkeys.password_hint')}
              </p>
              <Controller
                name="current_password"
                control={passwordForm.control}
                render={({ field, fieldState }) => (
                  <PasswordInput
                    {...field}
                    id="passkey-current-password"
                    autoComplete="current-password"
                    autoFocus
                    error={!!fieldState.error}
                    data-testid="passkeys-password-input"
                  />
                )}
              />
              {passwordForm.formState.errors.current_password && (
                <FieldError data-testid="passkeys-password-error">
                  {passwordForm.formState.errors.current_password.message}
                </FieldError>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                size="sm"
                loading={isVerifying}
                data-testid="passkeys-password-submit"
              >
                {t('passkeys.continue')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={cancelPassword}
                disabled={isVerifying}
                data-testid="passkeys-password-cancel"
              >
                {t('passkeys.cancel')}
              </Button>
            </div>
          </form>
        )}
        {isError && (
          <div className="flex items-center justify-between gap-4" data-testid="passkeys-error">
            <p className="text-sm text-destructive">{t('passkeys.load_error')}</p>
            <Button variant="secondary" size="sm" onClick={retry} data-testid="passkeys-retry">
              {t('passkeys.retry')}
            </Button>
          </div>
        )}
        {!isLoading && !isError && passkeys.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="passkeys-empty">
            {t('passkeys.empty')}
          </p>
        )}
        {passkeys.map((passkey) => (
          <div
            key={passkey.id}
            className="flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3"
            data-testid={`passkey-row-${passkey.id}`}
          >
            <div className="flex min-w-0 items-center gap-3">
              <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{passkey.name}</p>
                <p className="text-xs text-muted-foreground">
                  {t('passkeys.created', {
                    date: formatDate(passkey.created_at),
                  })}
                  {' · '}
                  {passkey.last_used_at
                    ? t('passkeys.last_used', {
                        date: formatDate(passkey.last_used_at),
                      })
                    : t('passkeys.never_used')}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deletePasskey(passkey.id)}
              loading={deletingId === passkey.id}
              disabled={needsPassword || isAdding || deletingId !== null}
              aria-label={t('passkeys.delete')}
              data-testid={`passkey-delete-${passkey.id}`}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
